import React, { CSSProperties, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import Section from '@hero/ui-kit/layout/Section';
import Button from '@hero/ui-kit/inputs/Button';
import H from '@hero/ui-kit/typography/H';
import P from '@hero/ui-kit/typography/P';
import useComponentPadding from '@hero/ui-kit/hooks/useComponentPadding';

import InlineLoader from '../../../components/InlineLoader';
import Details from './Details';
import TabBar from '../TabBar';
import { OrganizationRouteParams } from '../types';
import getDetailViewTitle from '../utils/getDetailViewTitle';
import DefaultLayout from '../../../components/DefaultLayout';
import useGetEnterpriseMemberDetails from '../api/useGetEnterpriseMemberDetails';
import useGetCancellationEnterpriseMembershipDetails from '../api/useGetCancellationEnterpriseMembershipDetails';
import useGetDischargedMember from '../../Retention/api/useGetDischargedMember';

const EnterpriseMemberDetails: React.FC = () => {
    const { id: organization_id, enrollment_id: id } =
        useParams<Required<OrganizationRouteParams>>();
    const navigate = useNavigate();

    const {
        data: enrollmentResponse,
        isLoading: isFetchingEnrollment,
        isError: isGetEnterpriseMemberError
    } = useGetEnterpriseMemberDetails({
        id: id ? +id : 0
    });

    const enrollment = enrollmentResponse?.data;
    const isCancelled = enrollment?.member?.status === 'CANCELLED';

    const memberId = !isGetEnterpriseMemberError && enrollment ? enrollment.member?.id : undefined;

    const {
        data: dischargedMemberResponse,
        isLoading: isFetchingDischargedMember,
        isError: isDischargedMemberError
    } = useGetDischargedMember({ member_id: memberId ? memberId : 0 }, !!memberId);

    const {
        data: orgCancelDetailsResponse,
        isLoading: isFetchingCancelDetails,
        isError: isOrgCancelDetailsError
    } = useGetCancellationEnterpriseMembershipDetails({ id: id ? +id : 0 }, isCancelled);

    const orgCancellationDetails = !isOrgCancelDetailsError
        ? orgCancelDetailsResponse?.data
        : undefined;
    const dischargedMember = !isDischargedMemberError ? dischargedMemberResponse?.data : undefined;

    const isFetching =
        isFetchingEnrollment || isFetchingCancelDetails || isFetchingDischargedMember;

    const handleBackToList = useCallback(() => {
        navigate(`/organizations/${organization_id}/members`, { replace: true });
    }, [navigate, organization_id]);

    const innerWrapPadding = useComponentPadding({ paddingVertical: 'large' });

    const headerWrapStyles: CSSProperties = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start'
    };

    return (
        <DefaultLayout>
            <Section
                subtheme={['regular', 'background']}
                paddingVertical="small"
                paddingHorizontal="regular"
            >
                <div style={headerWrapStyles}>
                    <H role="h5">{getDetailViewTitle(enrollment)}</H>
                    <Button variant="secondary" onClick={handleBackToList} noDefaultMargin>
                        Back To List
                    </Button>
                </div>
                <TabBar />
                <div style={innerWrapPadding}>
                    {isFetching ? (
                        <InlineLoader />
                    ) : enrollment ? (
                        <Details
                            enrollment={enrollment}
                            orgCancellationDetails={orgCancellationDetails}
                            dischargedMember={dischargedMember}
                        />
                    ) : (
                        <P>No membership data available.</P>
                    )}
                </div>
            </Section>
        </DefaultLayout>
    );
};

export default EnterpriseMemberDetails;
